import {Type} from '@angular/core';
import {Observable} from 'rxjs';

import {DefaultColumnComponent} from '../components/table/column-impl/default-column/default-column.component';
import {ColumnData, ColumnFieldComponent} from '../components/table/Column';
import {TableActionComponent} from '../components/table/Actions';

export enum SelectionMode {
  single = 'single',
  multiple = 'multiple',
  none= 'none',
  multipleWithBaseRoute = 'multiple_agile'
}


export interface TableConfig {
  version?: number;
  pageSize?: number;
  actions?: TableAction[];
  columnsFields?: TableField[];
  selectionMode?: SelectionMode;
  // When set, row selection is offered only to users holding one of these roles
  // (case-insensitive). Users with none of them get a non-selectable table.
  selectionAcceptRole?: string[];
  paginatorMode?: string;
  isRecordEnabled?: Function;
  rowDialog?: boolean; //questo serve per cliccare apre modale
  /**
   * Default per-row navigation target as routerLink commands; cells render as <a [routerLink]>.
   * Do NOT set on tables whose generic columns render interactive controls (buttons, inputs,
   * selects) — their content would be nested inside the cell anchor (invalid HTML, clicks hijacked).
   * Use a per-column TableField.link only on safe display columns in that case.
   */
  rowLink?: (row: any) => any[] | null;
  /**
   * Opt-in sticky "sum row" pinned to the foot of the table. Off by default — only lists that
   * set this render a footer row. Each total sits under the column its formatter targets, so the
   * column header self-labels it (e.g. Σ quantity under "Venduto"). Totals describe the WHOLE
   * filtered set (all pages), so feed them from a dedicated summary call, not the visible rows.
   *
   * Do NOT combine with editable/interactive generic columns: a footer cell only renders text.
   */
  summaryRow?: TableSummaryRow;
  /**
   * Opt-in grouped rows: a row the table renders as a collapsible group whose children appear
   * beneath it AS ORDINARY ROWS. Off by default — only lists that set this render anything
   * different.
   *
   * The table asks `isGroup` per row, so a response may legitimately mix group rows and ordinary
   * ones (the dashboard's grouped view renders a customer with a single order as a normal row).
   *
   * When a group is expanded the table splices `childrenOf(row)` into what it renders, so each
   * child goes through the SAME column definitions and cell components as every other row — it
   * is a real `mat-row`, aligned to the column grid by construction, not a nested list inside one
   * full-width cell. Expansion state lives in the CALLER, not here — the table only asks
   * `isExpanded`.
   */
  groupRow?: TableGroupRow;
}

/** A bag of named numeric totals for the sum row, keyed by metric name. */
export interface TableSummaryData {
  [metric: string]: number;
}

export interface TableSummaryRow {
  /** Emits the current totals, or `null` while loading / when there is nothing to show. */
  data$: Observable<TableSummaryData | null>;
  /**
   * Per-column renderer keyed by `TableField.id`: returns the formatted cell text (callers own
   * i18n + locale formatting). Columns with no entry render an empty footer cell. Put a leading
   * label (e.g. "Totali") inside the first column's formatter.
   */
  cells: { [columnId: string]: (data: TableSummaryData) => string };
  /**
   * When true, the sum row renders as ONE spanning cell: the first (populated) footer cell takes the
   * full row width and the other footer cells are hidden. Use for a summary whose text is a single
   * combined string in the leading column (e.g. stock "Totali — Qtà … · Valore …"). Default false =
   * per-column (each cell shows its own total under its column) — the reports sum row relies on this.
   */
  spanLeadingCell?: boolean;
}

export interface TableGroupRow {
  /** True when this row is an expandable group rather than an ordinary row. */
  isGroup: (row: any) => boolean;
  /** True when this group row is currently expanded. State is the caller's. */
  isExpanded: (row: any) => boolean;
  /**
   * The group's own child rows, or `undefined` when the row is not a group.
   *
   * A PURE function of the row, exactly like `isGroup`/`isExpanded` above, and for the same
   * reason: a `RouteConf`'s `groupRow` is a module-level const with no injector to reach a
   * service through. The children ride ON the row (the grouped endpoint ships them with it), so
   * this reads a field rather than fetching anything.
   *
   * The table splices these into what it RENDERS whenever the group is expanded, so a child is an
   * ordinary `mat-row` carrying the list's own columns -- not a nested component inside one
   * full-width cell. That is what makes an expanded child look exactly like a row of the flat
   * list: it goes through the same column definitions and the same cell components.
   */
  childrenOf: (row: any) => any[] | undefined;
  /**
   * Optional: what a plain click ON A GROUP ROW does. When present, the table calls it instead of
   * trying to select the row, and stops the click there.
   *
   * A group is not a record: it has no id, `rowLink` must answer `null` for it (its own cells can
   * carry interactive controls, which may never be wrapped in a row anchor), and `isRecordEnabled`
   * refuses to select it. That left the whole row inert apart from one small disclosure button,
   * while every other row on the list is clickable end to end -- and worse than inert, because the
   * click still bubbled to `DefaultListComponent`'s host listener, which acts on whatever selection
   * it happens to be holding.
   *
   * It receives the EVENT as well as the row, because what a click on a group means depends on its
   * modifiers and only the caller knows: a plain click expands, while ctrl/meta on a group of
   * orders means "open all of them". The table does not decide that -- it hands the gesture over
   * and stops it there, whatever the caller does with it.
   *
   * A PURE function of the row, like every other callback here and for the same reason: a
   * `RouteConf` is a module-level const with no injector, so this points at a module-level function
   * rather than a service method.
   */
  toggle?: (row: any, event: MouseEvent) => void;
  /**
   * Optional: true when a CHILD row spliced under `parent` should be highlighted -- e.g. the orders
   * of a group that contain a product the user searched for. Asked of every rendered child on every
   * rebuild, and the answer is always stamped (`__highlight`), so a highlight from a previous search
   * is cleared rather than left behind on a row object that outlived the reload.
   *
   * Only children are ever asked: a top-level row is never highlighted, which is what keeps a
   * single-record row looking exactly like a row of the flat list. A PURE function, like every
   * other callback here -- a `RouteConf` has no injector.
   */
  isHighlighted?: (child: any, parent: any) => boolean;
  /**
   * i18n key read to screen readers on a highlighted child, in a visually-hidden span in the first
   * value cell. The highlight is a colour AND a rail, both visual, so without this the reason the
   * row stands out is invisible to assistive tech (WCAG 1.4.1 / 1.3.1).
   */
  highlightLabelKey?: string;
  /**
   * Optional: emits whenever expansion state changed OUTSIDE this component -- i.e. whenever the
   * CALLER (the owner of `isExpanded`'s state) flips a row's flag. It is what tells this table to
   * rebuild the array it renders, and without it an expansion is invisible.
   *
   * Expansion state is deliberately kept outside this table (see `isExpanded`), so it is mutated
   * on the row IN PLACE. Ordinary change detection repaints the already-rendered bindings on that
   * row's own cells -- the chevron, `aria-expanded` -- but nothing re-derives WHICH ROWS EXIST:
   * the rendered array is rebuilt only when this component rebuilds it. So a plain click on a
   * disclosure control would flip the chevron while no child row ever appeared.
   *
   * (The same signal was needed, for a related reason, when children rendered through a
   * `cdkRowDef` `when` predicate instead: the CDK re-evaluates one only inside
   * `CdkTable.renderRows()`, which its own data-stream subscription calls once for an array
   * `[dataSource]` and never again. Measured at the time: toggling the flag plus two
   * `detectChanges()` passes left 0 detail rows in the DOM.)
   *
   * A plain `Observable`, not a `Subject`, so a `RouteConf`'s `groupRow` (a module-level const
   * with no injector -- see `isGroup`/`isExpanded`'s own constraint) can still point at it: the
   * caller owns a module-level exported `Subject` and hands this field its `.asObservable()`.
   */
  changes$?: Observable<void>;
  /**
   * Optional: called with the server's rows every time a new page lands, after the table has built
   * what it renders. The rows handed over are the SAME objects the table renders, so a caller that
   * learns something about them later (a second request, say) can stamp it on the row in place and
   * fire {@link changes$} -- which is the only way a `RouteConf` with no injector can enrich a row
   * after the fact. Fire-and-forget: the table does not wait for anything the caller starts here.
   */
  rowsLoaded?: (rows: any[]) => void;
  /**
   * Optional: a STABLE identity for a group, used for the token the table stamps on a group and
   * every child spliced under it (`rebuildRenderedRows`).
   *
   * Without it the token is the group's INDEX among the server's rows, which is stable enough for
   * one render pass and wrong for everything that outlives it. The token is what
   * `isPendingDelete` matches the store's marked row against, and the marked row is persisted to
   * the `${appVersion}_stat` blob -- so a positional token rehydrated on the next visit, or read
   * after a re-sort or a page change, matches whichever group now sits at that index: a DIFFERENT
   * customer, painted with the red pre-delete highlight with no delete pending. It is also the
   * key `getRowId` falls back to for a group row (an aggregate has no id by design), so a
   * positional value would key that row's selection state by position too.
   *
   * A PURE function of the row, like every other callback here and for the same reason: a
   * `RouteConf`'s `groupRow` is a module-level const with no injector. Return the value the server
   * actually grouped BY -- never a render-order-derived one. A nullish answer falls back to the
   * index, so an existing caller that declares none behaves exactly as before.
   */
  keyOf?: (row: any) => string | number | null | undefined;
}

export class TableAction {
  event?: string;
  icon?: string;
  label?: string;
  type?: string;
  position?: string;
  acceptRole?: string[];
  actionComponent?: Type<TableActionComponent>;
  /**
   * Per-row refusal for an action that stays VISIBLE: an i18n key explaining why this row's action
   * is unavailable, or nullish when it is available. The action component renders itself disabled
   * with that key as its tooltip, and refuses the click.
   *
   * Not `TableConfig.isRecordEnabled`, for two reasons: a false there HIDES the action, where a
   * refused one should say why; and `TableComponent.isEnabled` answers an action carrying
   * `acceptRole` from the role check alone, before it ever consults `isRecordEnabled`. Only action
   * components that read it honour it (`DeleteRowComponent`, `GroupedDeleteRowComponent`).
   */
  disabledReason?: (item: any) => string | null | undefined;
}


export const TableActionType = {
  RECORD: 'RECORD',
  GLOBAL: 'GLOBAL'
};

export interface TableEvent {
  name: string | undefined;
  data: any;
}

export enum ConfigTypeColumndField {
  number = 'number',
  text = 'text',
  email = 'email',
  tel = 'tel',
}


export class TableField {

  constructor(
    private $id: string,
    private $type?: Type<ColumnFieldComponent>,
    private $label?: string,
    private $field?: string,
    private $sortable = true,
    private $sortExpression?: string,
    public  attributes?: {[key: string]: any},
    private $editable = false,
    private $typeField?: ConfigTypeColumndField | string | any,
    private $tooltip?: ((item: ColumnData) => string),
    private $matIcon?: ((item: ColumnData) => string)) {

  }

  public get typeField(): string | Type<any> {
    return this.$typeField != null ? this.$typeField :  null;
  }

  public set typeField(typeField: string | Type<any>) {
    this.$typeField = typeField;
  }




  public get sortable(): boolean {
    return this.$sortable != null ? this.$sortable :  true;
  }

  public set sortable(sortable: boolean) {
    this.$sortable = sortable;
  }

  public get editable(): boolean {
    return this.$editable != null ? this.$editable :  true;
  }

  public set editable(editable: boolean) {
    this.$editable = editable;
  }

  public get sortExpression(): string {
    return this.$sortExpression || this.field;
  }


  public get id() {
    return this.$id;
  }

  public get label() {
    return this.$label || this.id;
  }

  public set label(label: string) {
    this.$label = label;
  }

  public get field() {
    return this.$field || this.id;
  }

  public set field(field: string) {
    this.$field = field;
  }

  public get type(): Type<ColumnFieldComponent> {
    return this.$type || DefaultColumnComponent;
  }

  public set type(type: Type<ColumnFieldComponent>) {
    this.$type = type;
  }

  get tooltip(): ((item: ColumnData) => string) | undefined {
    return this.$tooltip;
  }

  set tooltip(value: ((item: ColumnData) => string) | undefined) {
    this.$tooltip = value;
  }

  get matIcon(): ((item: ColumnData) => string) | undefined {
    return this.$matIcon;
  }

  set matIcon(value: ((item: ColumnData) => string) | undefined) {
    this.$matIcon = value;
  }

  /** Per-column navigation target (routerLink commands); overrides TableConfig.rowLink for this cell. */
  public get link(): ((row: any) => any[] | null) | undefined {
    return this.attributes?.['link'];
  }
}

